import { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface WorkerStatus {
  employeeId: string;
  employeeName: string;
  employeePhoto?: string;
  workstationId?: string;
  workstationName: string;
  currentOrderNumber?: string;
  currentStepName?: string;
  progressCompleted: number;
  progressTotal: number;
  status: 'working' | 'on_break' | 'idle' | 'behind';
  timeOnCurrentTaskMinutes?: number;
  breakType?: string;
  breakStartedAt?: string;
}

interface FlaggedItem {
  id: string;
  step_name: string;
  order_number: string;
  employee_name: string;
  flag_reason: string;
  flag_notes?: string;
  created_at: string;
}

interface FloorStatus {
  workers: WorkerStatus[];
  flaggedItems: FlaggedItem[];
  timestamp: string;
}

export default function ProductionFloorPage() {
  const [floorStatus, setFloorStatus] = useState<FloorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_auth_token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  const loadFloorStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/production/floor-status`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setFloorStatus(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load floor status:', err);
      setError('Failed to load floor status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFloorStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadFloorStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'on_break':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'behind':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatBreakTime = (startedAt: string) => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - start.getTime()) / 60000);
    return `${diffMinutes} min`;
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <div className="text-gray-500">Loading floor status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Production Floor</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Live view of all active workers
          </p>
        </div>
        <button
          onClick={loadFloorStatus}
          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {floorStatus?.workers.filter((w) => w.status === 'working').length || 0}
          </div>
          <div className="text-sm text-gray-500">Working</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-yellow-600">
            {floorStatus?.workers.filter((w) => w.status === 'on_break').length || 0}
          </div>
          <div className="text-sm text-gray-500">On Break</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-red-600">
            {floorStatus?.flaggedItems.length || 0}
          </div>
          <div className="text-sm text-gray-500">Flagged Items</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {floorStatus?.workers.length || 0}
          </div>
          <div className="text-sm text-gray-500">Total Clocked In</div>
        </div>
      </div>

      {/* Workers Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Active Workers</h2>
        {!floorStatus?.workers.length ? (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-8 text-center text-gray-500">
            No workers currently clocked in
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {floorStatus.workers.map((worker) => (
              <div
                key={worker.employeeId}
                className={`bg-white dark:bg-gray-800 rounded-xl p-4 border-2 ${
                  worker.status === 'behind'
                    ? 'border-red-500'
                    : worker.status === 'on_break'
                    ? 'border-yellow-500'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                {/* Worker Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-lg font-bold text-gray-600 dark:text-gray-300">
                    {worker.employeeName.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white truncate">
                      {worker.employeeName}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {worker.workstationName}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(worker.status)}`}>
                    {worker.status === 'on_break' && worker.breakType
                      ? worker.breakType.charAt(0).toUpperCase() + worker.breakType.slice(1)
                      : worker.status.replace('_', ' ')}
                  </span>
                </div>

                {/* Current Task */}
                {worker.currentOrderNumber && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-3">
                    <div className="text-sm text-gray-500">Current Task</div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Order #{worker.currentOrderNumber}
                    </div>
                    {worker.currentStepName && (
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        {worker.currentStepName}
                      </div>
                    )}
                  </div>
                )}

                {/* Progress */}
                {worker.progressTotal > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Progress</span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {worker.progressCompleted}/{worker.progressTotal}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 rounded-full"
                        style={{
                          width: `${(worker.progressCompleted / worker.progressTotal) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Break Timer */}
                {worker.status === 'on_break' && worker.breakStartedAt && (
                  <div className="text-center text-sm text-yellow-600 dark:text-yellow-400">
                    Break: {formatBreakTime(worker.breakStartedAt)}
                  </div>
                )}

                {/* Time on Task */}
                {worker.timeOnCurrentTaskMinutes !== null && worker.timeOnCurrentTaskMinutes !== undefined && (
                  <div className="text-center text-sm text-gray-500">
                    Time on task: {worker.timeOnCurrentTaskMinutes} min
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Flagged Items */}
      {floorStatus?.flaggedItems && floorStatus.flaggedItems.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Flagged Items Requiring Attention
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-900 overflow-hidden">
            <table className="w-full">
              <thead className="bg-red-50 dark:bg-red-900/20">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-red-800 dark:text-red-200">Order</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-red-800 dark:text-red-200">Step</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-red-800 dark:text-red-200">Worker</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-red-800 dark:text-red-200">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-red-800 dark:text-red-200">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {floorStatus.flaggedItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      #{item.order_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {item.step_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {item.employee_name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded text-xs">
                        {item.flag_reason}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {item.flag_notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Last Updated */}
      {floorStatus?.timestamp && (
        <div className="mt-6 text-center text-sm text-gray-400">
          Last updated: {new Date(floorStatus.timestamp).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
